import { Box, Paper, Stepper, Step, StepLabel, Typography } from '@mui/material';
import { Button, Spinner } from 'reactstrap';
import Swal from 'sweetalert2';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormikStepper } from '../components/hooks';
import FormResetButton from '../components/steps/components/FormResetButton';
import { getSignReminderHistory, sendSigningLinksToTenants, updateLeaseById, validateStep } from '../components/utils';
import { FormikProviderWrapper } from './providers/FormikProviderWrapper';
import { lease_modes_allowed, validators } from '../components/config';

export const LeaseStepper = () => {
    const { mode = "", property_id, unit_no, building_name } = useParams();
    const isValidMode = Object.keys(lease_modes_allowed).includes(mode);
    const modeLabel = isValidMode ? lease_modes_allowed[mode] : '';

    const {
        currentStep,
        setCurrentStep,
        nextStep,
        isSubmitting,
        setIsSubmitting,
        leaseSteps,
        leaseId,
        prevStep,
        isOnPreviousTab,
        setIsOnPreviousTab
    } = useFormikStepper();

    const { values, submitForm, validateForm, setTouched } = useFormikContext();

    // Automatic signing reminders already sent for this lease (shown under "Resend Lease Links").
    const [reminderHistory, setReminderHistory] = useState([]);
    const historyLeaseId = leaseId || values.leaseId;
    const loadReminderHistory = useCallback(async () => {
        if (!historyLeaseId) return;
        try {
            setReminderHistory(await getSignReminderHistory(historyLeaseId));
        } catch {
            setReminderHistory([]);
        }
    }, [historyLeaseId]);
    useEffect(() => {
        if (currentStep === 3) loadReminderHistory();
    }, [currentStep, loadReminderHistory]);

    const handleSubmitCurrentStep = async () => {
        try {
            setIsSubmitting(true);

            if (isOnPreviousTab) {
                const isStepValid = await validateStep(validators.step1, values);
                if (!isStepValid) return false;

                await updateLeaseById(leaseId, String(currentStep), values);
                if (currentStep == 1) setIsOnPreviousTab(false);

                setCurrentStep(currentStep + 1);
            } else {
                const errors = await validateForm();
                await setTouched(
                    Object.keys(errors).reduce((acc, key) => {
                        acc[key] = true;
                        return acc;
                    }, {})
                );

                if (Object.keys(errors).length === 0) {
                    await submitForm();
                    nextStep();
                } else {
                    return false;
                }
            }

            return true;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to submit the current step';
            Swal.fire({
                icon: 'error',
                title: 'Submission Error',
                text: errorMessage,
            });
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendLeaseLinks = async () => {
        try {
            const targetLeaseId = leaseId || values.leaseId;
            if (!targetLeaseId) {
                throw new Error('Lease ID is required to send signing links');
            }

            const tenants = values.tenants || [];
            if (tenants.length === 0) {
                throw new Error('No tenants found to resend links to');
            }

            const { value: emailInputs } = await Swal.fire({
                title: 'Update Tenant Emails',
                html: tenants
                    .map(
                        (tenant, index) => `
                        <div style="margin-bottom: 15px;">
                            <label style="display: block; margin-bottom: 5px;">
                                Email for ${tenant.name}
                            </label>
                            <input 
                                type="email" 
                                id="email_${index}" 
                                name="email_${index}" 
                                placeholder="Enter email address" 
                                value="${tenant.email || ''}"
                                style="width: 100%; padding: 8px; border-radius: 4px; border: 1px solid #ccc;"
                                required
                            >
                        </div>
                    `
                    )
                    .join(''),
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: 'Send Links',
                preConfirm: () => {
                    const emails = {};
                    tenants.forEach((_, index) => {
                        const input = document.getElementById(`email_${index}`);
                        emails[`email_${index}`] = input.value;
                    });
                    for (let i = 0; i < tenants.length; i++) {
                        const email = emails[`email_${i}`];
                        if (!email) {
                            Swal.showValidationMessage(`Email for ${tenants[i].name} is required`);
                            return false;
                        }
                        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                            Swal.showValidationMessage(`Invalid email for ${tenants[i].name}`);
                            return false;
                        }
                    }
                    return emails;
                },
            });

            if (!emailInputs) return;

            const updatedTenants = tenants.map((tenant, index) => ({
                ...tenant,
                email: emailInputs[`email_${index}`],
                id: tenant.id || (index + 1).toString(),
                signature: tenant.signature || '',
                date: tenant.date || '',
            }));

            const response = await sendSigningLinksToTenants(targetLeaseId, { tenants: updatedTenants });
            if (response?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Emails Sent!',
                    text: `Signing links were sent to ${updatedTenants.length} tenant(s) successfully.`,
                });
                loadReminderHistory();
            } else {
                throw new Error(response?.message || 'Failed to send emails');
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err.message || err?.data?.message || 'An error occurred while sending emails.',
            });
        }
    };

    const CurrentStepComponent = leaseSteps[currentStep]?.component || (() => <div>Loading...</div>);

    return (
        <Box className="lease-steps-wrapper" mt={10} mb={10} sx={{ width: '100%', p: 3 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="center" position="relative" mb={4}>
                    <Typography
                        variant="h6"
                        align="center"
                        sx={{
                            fontFamily: `'Times New Roman', Times, serif`,
                            fontWeight: 800,
                        }}
                    >
                        {isValidMode ? modeLabel : 'Lease Management'}: {building_name ? `${building_name} - Unit ${unit_no}` : `Unit ${unit_no}`}
                    </Typography>

                    <Box position="absolute" right={0}>
                        <FormResetButton
                            property_id={property_id}
                            building_name={building_name}
                            unit_no={unit_no}
                            mode={isValidMode ? mode : null}
                            leaseId={leaseId}
                        />
                    </Box>
                </Box>

                <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 4 }}>
                    {leaseSteps.map((step, index) => (
                        <Step key={index}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <CurrentStepComponent />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
                    {currentStep < 3 && (
                        <Button
                            disabled={currentStep === 0}
                            onClick={prevStep}
                            outline={true}
                            color="secondary"
                            type="button"
                        >
                            Back
                        </Button>
                    )}
                    {currentStep === 3 ? (
                        <Button
                            color="info"
                            onClick={handleResendLeaseLinks}
                            outline={false}
                            type="button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Resending Lease Links...' : 'Resend Lease Links'}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleSubmitCurrentStep}
                            outline={false}
                            color="success"
                            type="button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner size="sm" />{' '}
                                    {currentStep === 0 || currentStep === 1
                                        ? 'Submitting Step...'
                                        : currentStep === 2
                                            ? 'Submitting Lease Details...'
                                            : 'Saving Changes...'}
                                </>
                            ) : (
                                currentStep === 0 || currentStep === 1
                                    ? (isOnPreviousTab ? 'Next' : 'Submit & Continue')
                                    : currentStep === 2
                                        ? 'Submit & Continue'
                                        : 'Complete Process'
                            )}
                        </Button>
                    )}
                </Box>

                {currentStep === 3 && reminderHistory.length > 0 && (
                    <Box sx={{ mt: 2, ml: 'auto', maxWidth: 460, border: '1px solid #e4e9f2', borderRadius: 2, p: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>
                            Reminder history
                        </Typography>
                        <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 0.5, p: 0, maxHeight: 180, overflowY: 'auto' }}>
                            {reminderHistory.map((r, i) => (
                                <Box component="li" key={`${r.stepKey}-${r.email}-${i}`} sx={{ display: 'flex', gap: 1, fontSize: 13, py: 0.25 }}>
                                    <span style={{ fontWeight: 600 }}>{r.stepLabel}</span>
                                    <span style={{ color: '#33415c', flex: 1 }}>{r.tenantName || r.email}</span>
                                    <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                                        {new Date(r.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

const LeaseCreator = () => {
    return (
        <FormikProviderWrapper>
            <LeaseStepper />
        </FormikProviderWrapper>
    );
};

export { LeaseCreator };